/**
 * Applicant Form_Renderer — ApplicationFormContainer
 * (application-form-builder spec, task 23.4).
 *
 * The stateful shell around the presentational {@link FormRenderer}. It owns the
 * data flow and the save-draft / submit / lock orchestration the renderer
 * deliberately left to its container, talking ONLY to the `applicationFormClient`
 * transport seam (never AppSync directly), so the surface flips mock→live with
 * no call-site change.
 *
 * What it wires:
 *   1. **Load** — `getActiveFormVersion(competitionId, divisionKey)` for the
 *      structure the applicant completes, and `getMyApplicationResponse(...)`
 *      for any previously saved answers. The captured `FormVersion.id` is
 *      recorded and replayed on every write so values are always interpreted
 *      against the version they were authored under (Req 6.5).
 *   2. **Restore** — the saved `ApplicationResponse.values` seed the form's
 *      `defaultValues`, so reopening a draft restores the applicant's prior
 *      answers into their Custom_Fields (Req 6.2).
 *   3. **Save draft** — reads the live form values WITHOUT triggering the strict
 *      schema, and calls `saveDraft`, which persists DRAFT without enforcing
 *      required-field completeness (Req 6.1).
 *   4. **Submit** — runs the full client validation (the renderer's React Hook
 *      Form + the shared `deriveZodSchema`) via `form.handleSubmit`, then the
 *      document gate (`missingRequiredDocuments` — the same pure rule the
 *      server's submit path runs), and only then calls `submit`, which the
 *      server re-validates against the captured version before transitioning to
 *      SUBMITTED (Req 6.3, 7.1, 7.6, 20.3).
 *   5. **Locks** — once the response is SUBMITTED, Immutable_Fields are rendered
 *      read-only (Req 6.4); a Form_Page that is an APPROVED approval-required
 *      page is rendered read-only via the shared `isPageEditableByApplicant`
 *      predicate (`=== false` ⇒ locked, Req 21.4).
 *   6. **Bound writes** — the bound-control adapter is passed through to the
 *      renderer with the Division, Season, and Title-preference write callbacks
 *      wired to the client (`selectDivision` / `selectSeason` /
 *      `saveTitlePreferences`); these route to their canonical entities and
 *      never the response `values` map (Req 17.2).
 *
 * Phase-one relocation capture (director-form-application-cutover, task 8.11):
 *   On the flag-on path this container is mounted by `ApplyHost` /
 *   `ApplyRendererSurface` — the legacy `ApplicationFlow` is mounted only when
 *   the flag is OFF, so this container is never reached on the legacy path and
 *   the flag-off `ApplicantYearProfile` writes stay byte-for-byte intact
 *   (cutover Req 10.6). The relocated phase-one fields — marital status
 *   (`phase1_maritalStatus`), the rules-and-agreement signature
 *   (`phase1_rulesAgreementSignature`), and the eligibility-affidavit signatory
 *   (`phase1_eligibilityAffidavit_*`) — are director-authored CUSTOM fields
 *   carrying those canonical ids (see `builder/starterPages.ts`). The renderer's
 *   custom-field capture (`FieldRenderer` → React Hook Form, keyed by `field.id`)
 *   flows their answers into the `values` map that `saveDraft` / `submit`
 *   persist to `Application_Response.values` through the `applicationFormClient`
 *   transport ONLY — this container holds no `ApplicantYearProfile` write path,
 *   so a relocated field never touches AYP (cutover Req 3.2–3.4, 3.6, 10.1–10.3).
 *   A required signatory CUSTOM field that is not accepted fails the shared
 *   `deriveZodSchema` / `validateResponse` gate, so `form.handleSubmit` never
 *   reaches `submitValidated`: the submit is blocked, every offending field is
 *   named in the renderer's `SubmissionErrorSummary`, and React Hook Form retains
 *   the entered answers (cutover Req 3.7). When a `saveDraft` / `submit` write
 *   fails, the catch path surfaces `actionError` while React Hook Form keeps the
 *   entered values and nothing is written to AYP (cutover Req 10.5).
 *
 * Validates: Requirements 6.1, 6.2, 6.3, 6.4, 6.5, 20.3, 21.4;
 *   director-form-application-cutover Requirements 3.1, 3.2, 3.3, 3.4, 3.6, 3.7,
 *   10.1, 10.2, 10.3, 10.5, 10.6
 */

import * as React from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import CrownLoader from '@/components/ui/CrownLoader';
import { applicationFormClient } from '@/lib/application-form/client';
import type {
  ApplicationResponse,
  FieldValidationError,
  FormVersion,
} from '@/lib/application-form/client';
import type { ApplicationResponseValues } from '@/lib/application-form/deriveZodSchema';

import { FormRenderer } from './FormRenderer';
import { CompletionBar } from './CompletionBar';
import { PhaseDeadlineBanner } from './PhaseDeadlineBanner';
import {
  classifyPhaseLockError,
  derivePhaseLockState,
  phaseLockMessage,
  type PhaseLockState,
} from './validationMessaging';
import { useApplicationCompletion } from '@/components/application-form/hooks/useApplicationCompletion';
import type { CompletionInput } from '@/lib/application-completion/computeCompletion';
import type { BoundControlAdapter } from './boundControls';
import type { FileUploadAdapter } from './fieldRegistry';
import {
  isPageEditableByApplicant,
  missingRequiredDocuments,
  SubmissionStatus,
  type DocumentCatalogEntry,
  type Division,
  type FormFieldDef,
  type FormVersionSnapshot,
  type PageApprovalStatus,
  type SnapshotPage,
} from './pure';

/**
 * Auto-managed Documents section (apply-document-uploads, Approach A).
 *
 * A director configures required documents in the per-competition
 * Document_Catalog only — they do NOT also have to add a DOCUMENT_SLOT field to
 * the form structure. When the catalog has entries but the published form
 * carries no DOCUMENT_SLOT field, the container injects this synthetic,
 * applicant-visible "Documents" section so the uploader always appears and the
 * submit gate is satisfiable. The DOCUMENT_SLOT control renders the ENTIRE
 * in-scope catalog as slots from a single field, so one section is the whole
 * uploader. Deterministic ids keep the section stable across renders. If a
 * director DID author their own DOCUMENT_SLOT field, the form is left untouched
 * (no duplicate section).
 */
const AUTO_DOCUMENTS_PAGE_ID = '__auto_documents__';
const AUTO_DOCUMENTS_FIELD_ID = '__auto_documents_slot__';

/** Whether any field in the snapshot already carries a DOCUMENT_SLOT bound field. */
function snapshotHasDocumentSlot(snapshot: FormVersionSnapshot): boolean {
  return snapshot.pages.some((page) =>
    page.fields.some((field) => field.bindingTarget === 'DOCUMENT_SLOT'),
  );
}

/** Append the synthetic, applicant-visible Documents section to a snapshot. */
function withAutoDocumentsPage(snapshot: FormVersionSnapshot): FormVersionSnapshot {
  const maxOrder = snapshot.pages.reduce((max, page) => Math.max(max, page.displayOrder), 0);
  const documentsField: FormFieldDef = {
    id: AUTO_DOCUMENTS_FIELD_ID,
    label: 'Required documents',
    fieldCategory: 'BOUND',
    bindingTarget: 'DOCUMENT_SLOT',
    required: false,
    visibleDivisionKeys: null,
    audienceSet: ['APPLICANT', 'DIRECTOR'],
  };
  const documentsPage: SnapshotPage = {
    id: AUTO_DOCUMENTS_PAGE_ID,
    title: 'Documents',
    helpText: 'Upload the documents required for your application.',
    displayOrder: maxOrder + 1,
    visibleDivisionKeys: null,
    audienceSet: ['APPLICANT', 'DIRECTOR'],
    phase: 'PHASE_1',
    requiresDirectorApproval: false,
    fields: [documentsField],
  };
  return { ...snapshot, pages: [...snapshot.pages, documentsPage] };
}

export interface ApplicationFormContainerProps {
  /** The competition whose Application_Form the applicant is completing. */
  competitionId: string;
  /** The applicant's assigned Division key (drives per-division visibility + the document gate). */
  divisionKey: string;
  /** The competition's Divisions; only `active ∧ appliesToApplications` ones are selectable/visible. */
  activeDivisions: readonly Division[];
  /**
   * The bound-control read-model + Document-slot upload seam (Req 17, 19, 20).
   * The container injects the Division, Season, and Title-preference WRITE
   * callbacks wired to the client (`selectDivision` / `selectSeason` /
   * `saveTitlePreferences`); anything the host supplies here (including those
   * callbacks) takes precedence, so a host can override the wiring or provide a
   * read-only preview adapter.
   */
  boundControls?: BoundControlAdapter;
  /** Optional FILE_UPLOAD capture adapter for Custom_Field file inputs. */
  fileUploadAdapter?: FileUploadAdapter;
  /**
   * The applicant's per-page approval statuses, keyed by `formPageId`. An
   * approval-required page that is APPROVED is rendered read-only
   * (`isPageEditableByApplicant === false`, Req 21.4); a missing entry is
   * treated as the PENDING default (editable).
   */
  pageApprovals?: Readonly<Record<string, PageApprovalStatus>>;
  /**
   * The ids of fields marked `immutableOnSubmit`. Once the response is
   * SUBMITTED, these are rendered read-only so their value can no longer change
   * (Req 6.4). The Form_Version snapshot does not carry the flag, so the host
   * supplies it from the Application_Form definition.
   */
  immutableFieldIds?: readonly string[];
  /**
   * The Season's Phase_1_Close_Date (its intake window `closesAt`), an absolute
   * ISO-8601 instant or null/absent. Displayed on the Phase 1 surface (before
   * approval) via the shared viewer-local formatter; omitted when absent
   * (season-phase-deadlines Req 8.1, 8.2, 13.1, 13.3).
   */
  phaseOneCloseAt?: string | null;
  /**
   * The Season's Phase_2_Due_Date, an absolute ISO-8601 instant or null/absent.
   * Displayed on the Phase 2 surface for an APPROVED Application via the shared
   * viewer-local formatter; omitted when absent or when not APPROVED
   * (season-phase-deadlines Req 8.3, 8.4, 8.5, 13.2, 13.4).
   */
  phaseTwoDueDate?: string | null;
  /**
   * A human-readable Season label (e.g. `2026 Season`) used to identify the
   * Season in the phase-closed / locked message (season-phase-deadlines
   * Req 9.5, 16.5, 17.1). Absent ⇒ the message falls back to "this Season".
   */
  seasonLabel?: string | null;
  /** Notified after a successful save-draft, with the persisted response. */
  onSavedDraft?: (response: ApplicationResponse) => void;
  /** Notified after a successful submit, with the SUBMITTED response. */
  onSubmitted?: (response: ApplicationResponse) => void;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; version: FormVersion };

type ActionKind = 'draft' | 'submit';

/**
 * The outcome of a `handleSaveDraft` attempt, so the APPROVED-Phase-2 auto-save
 * indicator can reflect reality instead of always claiming success. `skipped` is
 * a no-op attempt (a save already in flight, no loaded version, or a read-only
 * locked Application) — it must NOT flip the indicator to saved OR failed.
 */
type SaveOutcome = 'saved' | 'error' | 'skipped';

/**
 * Debounced auto-save for the APPROVED Phase_2 editing mode
 * (title-approval-tracking / application-form-builder Req 16). Once an
 * application is APPROVED the contestant continues on their editable Phase_2
 * pages with NO "Submit application" step — their edits persist automatically.
 * This subscribes to user-driven form changes and calls the container's
 * save-draft path after a short idle delay (the backend `persistResponse`
 * preserves the APPROVED status on such a save), rendering a subtle live status
 * in place of the submit/save-draft buttons.
 */
function ApprovedPhaseTwoAutoSave({
  form,
  onSave,
  saving,
}: {
  form: UseFormReturn<ApplicationResponseValues>;
  onSave: (form: UseFormReturn<ApplicationResponseValues>) => Promise<SaveOutcome>;
  saving: boolean;
}): React.ReactElement {
  // Track the LAST real save outcome so the indicator never claims "All changes
  // saved." on a save that actually failed. A `skipped` attempt (a save already
  // in flight) leaves the prior state untouched — the in-flight save owns the
  // next transition.
  const [lastOutcome, setLastOutcome] = React.useState<'none' | 'saved' | 'error'>('none');

  React.useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const subscription = form.watch((_values, info) => {
      // React only to a user edit ('change'); ignore programmatic resets/hydration.
      if (info.type !== 'change') return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void onSave(form).then((outcome) => {
          if (outcome !== 'skipped') setLastOutcome(outcome);
        });
      }, 800);
    });
    return () => {
      if (timer) clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, [form, onSave]);

  const failed = !saving && lastOutcome === 'error';
  const message = saving
    ? 'Saving…'
    : lastOutcome === 'error'
      ? 'We could not save your latest changes. We will keep trying as you edit.'
      : lastOutcome === 'saved'
        ? 'All changes saved.'
        : 'Your changes save automatically.';

  return (
    <p
      aria-live="polite"
      data-testid="application-form-autosave-status"
      className={`text-body-sm ${failed ? 'text-destructive-text' : 'text-muted-foreground'}`}
    >
      {message}
    </p>
  );
}

/**
 * The contestant CompletionBar header (application-completion-tracker §C2/§C3).
 *
 * Rendered as the FormRenderer `header` render-prop so it sits at the very top
 * of the "Your application" surface, above the dynamic sections (Requirement
 * 7.1). It subscribes to the container's live React Hook Form via
 * `useApplicationCompletion(form, …)`, so the bar tracks edits without any new
 * fetch — the snapshot, division, catalog and uploads are the data the container
 * already holds. UNAVAILABLE ⇒ the bar renders nothing (Requirement 8.2).
 */
function CompletionBarHeader({
  form,
  snapshot,
  divisionKey,
  documentCatalog,
  uploadedDocuments,
}: {
  form: UseFormReturn<ApplicationResponseValues>;
  snapshot: CompletionInput['snapshot'];
  divisionKey: string;
  documentCatalog: CompletionInput['documentCatalog'];
  uploadedDocuments: CompletionInput['uploadedDocuments'];
}): React.ReactElement {
  const result = useApplicationCompletion({
    form,
    snapshot,
    divisionKey,
    documentCatalog,
    uploadedDocuments,
  });
  return <CompletionBar result={result} />;
}

export function ApplicationFormContainer({
  competitionId,
  divisionKey,
  activeDivisions,
  boundControls,
  fileUploadAdapter,
  pageApprovals,
  phaseOneCloseAt,
  phaseTwoDueDate,
  seasonLabel,
  onSavedDraft,
  onSubmitted,
}: ApplicationFormContainerProps): React.ReactElement {
  const [load, setLoad] = React.useState<LoadState>({ status: 'loading' });
  const [response, setResponse] = React.useState<ApplicationResponse | null>(null);
  const [busy, setBusy] = React.useState<ActionKind | null>(null);
  /** Server-side + document-gate failures (distinct from the renderer's own client validation summary). */
  const [submitErrors, setSubmitErrors] = React.useState<FieldValidationError[]>([]);
  /** A transport/unexpected error from a save or submit attempt. */
  const [actionError, setActionError] = React.useState<string | null>(null);
  /**
   * A phase-closed / locked lock derived from a server rejection (season-phase-
   * deadlines Req 9.5). The client mirror pre-empts most rejections, but when the
   * authoritative server returns a `PhaseClosed` / `Locked` error we map it to the
   * specific phase-and-Season message rather than a generic error.
   */
  const [serverLock, setServerLock] = React.useState<PhaseLockState | null>(null);

  // ---- Load the active version + any saved response (Req 6.2, 6.5) ---------
  React.useEffect(() => {
    let cancelled = false;
    setLoad({ status: 'loading' });
    setResponse(null);
    setSubmitErrors([]);
    setActionError(null);
    setServerLock(null);

    Promise.all([
      applicationFormClient.getActiveFormVersion(competitionId, divisionKey),
      applicationFormClient.getMyApplicationResponse(competitionId),
    ])
      .then(([version, saved]) => {
        if (cancelled) return;
        setResponse(saved);
        setLoad({ status: 'ready', version });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoad({
          status: 'error',
          message: err instanceof Error ? err.message : 'Failed to load this application.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [competitionId, divisionKey]);

  // ---- Derived view state ---------------------------------------------------
  const version = load.status === 'ready' ? load.version : null;
  // The snapshot/values cross from the transport DTOs into the pure-module
  // shapes the renderer consumes; they are structurally identical (the only gap
  // is the FieldType string-union vs enum), so a single localized cast bridges.
  const snapshot = (version?.snapshot ?? null) as FormVersionSnapshot | null;
  const formVersionId = version?.id ?? null;
  // The container is now only mounted for editable Application States
  // (IN_PROGRESS / CHANGES_REQUESTED); the read-only SUBMITTED/APPROVED/REJECTED
  // surface is owned by `ApplicationStatusView` (applicant-application-flow
  // Req 6.2). `submissionStatus` is still passed to the renderer for its
  // phase/visibility context.
  const submissionStatus = (response?.submissionStatus ?? 'DRAFT') as SubmissionStatus;
  // APPROVED contestants continue on their editable Phase_2 pages: their edits
  // auto-save and there is NO "Submit application" step (the application is
  // already submitted + approved). The backend `persistResponse` preserves the
  // APPROVED status on a saveDraft from this state, so auto-save never
  // un-approves the contestant.
  const isApproved = submissionStatus === SubmissionStatus.APPROVED;

  // ---- Phase deadlines + effective lock mirror (season-phase-deadlines) -----
  // The client mirrors the server-derived read fields (`effectiveLock` /
  // `isPhase1Open` / `isPhase2Unlocked`, from getMyApplicationResponse) plus the
  // Director `manualLockOverride` to decide whether the Application is read-only
  // and which submit is prevented; the server remains authoritative (R16.5,
  // R17.2). A server `PhaseClosed` / `Locked` rejection (mapped into `serverLock`)
  // takes precedence so the SPECIFIC message is shown even in the edge case the
  // proactive mirror could not pre-empt (R9.5).
  const derivedLock = React.useMemo<PhaseLockState | null>(
    () =>
      derivePhaseLockState({
        submissionStatus,
        effectiveLock: response?.effectiveLock,
        isPhase1Open: response?.isPhase1Open,
        isPhase2Unlocked: response?.isPhase2Unlocked,
        manualLockOverride: response?.manualLockOverride ?? null,
        seasonLabel,
      }),
    [submissionStatus, response, seasonLabel],
  );
  const lockState = serverLock ?? derivedLock;
  const isLocked = lockState !== null;

  // The deadline shown for the current surface: the Phase_1_Close_Date before
  // approval (the Phase 1 surface), or the Phase_2_Due_Date for an APPROVED
  // Application (the Phase 2 surface). Absent values render nothing (R8, R13).
  const displayedDeadline = isApproved ? phaseTwoDueDate ?? null : phaseOneCloseAt ?? null;
  const deadlineLabel = isApproved ? 'Phase 2 due' : 'Phase 1 closes';

  const defaultValues = React.useMemo(
    () => (response?.values ?? {}) as ApplicationResponseValues,
    [response],
  );

  // ---- Bound-control adapter: inject the client-wired canonical writes ------
  const wiredBoundControls = React.useMemo<BoundControlAdapter>(
    () => ({
      ...boundControls,
      // Rehydrate the applicant's saved ranked Title_Preferences (response slot,
      // not `values`) so the control seeds the saved rows on re-entry instead of
      // blank. An explicit adapter value (e.g. from the surface) still wins.
      titlePreferences:
        boundControls?.titlePreferences ?? response?.titlePreferences ?? undefined,
      // The settled Title after approval (Participation.approvedTitle, surfaced
      // on the applicant's own response) + the approved flag, so the Title
      // control drops the ranked-preference editor for a single read-only Title.
      approvedTitle: boundControls?.approvedTitle ?? response?.approvedTitle ?? undefined,
      applicationApproved:
        boundControls?.applicationApproved ??
        response?.submissionStatus === 'APPROVED',
      // The applicant's overall submission status, threaded so the document
      // renderer applies the SAME Phase-2 visibility rule as `Phase2Container`:
      // while not `'APPROVED'`, Phase_2 slots are hidden; once `'APPROVED'` they
      // are revealed (Phase_1 slots are always shown). This client hide mirrors
      // `Phase2Container.tsx` and is a UX affordance — the presign server gate is
      // authoritative (application-builder-documents Req 5.1, 5.3, 5.4).
      submissionStatus: boundControls?.submissionStatus ?? response?.submissionStatus ?? undefined,
      onSelectDivision:
        boundControls?.onSelectDivision ??
        ((nextDivisionKey: string) =>
          applicationFormClient.selectDivision(competitionId, nextDivisionKey)),
      onSelectSeason:
        boundControls?.onSelectSeason ??
        ((competitionYear: number) =>
          applicationFormClient.selectSeason(competitionId, competitionYear)),
      onSaveTitlePreferences:
        boundControls?.onSaveTitlePreferences ??
        ((ranked: string[]) =>
          applicationFormClient.saveTitlePreferences(competitionId, ranked)),
    }),
    [boundControls, competitionId, response],
  );

  // A submit precheck records missing-document errors in `submitErrors`, but that
  // banner is otherwise only recomputed on the next submit/save. Clear it when the
  // uploaded-document set changes so resolving a flagged slot (uploading the
  // missing file) immediately dismisses the now-stale "required document is
  // missing" message instead of lingering while the slot already reads as
  // provided. Keyed on the uploaded-docs array (stable until it actually changes).
  const uploadedDocumentsForEffect = wiredBoundControls.uploadedDocuments;
  React.useEffect(() => {
    setSubmitErrors([]);
  }, [uploadedDocumentsForEffect]);

  // ---- Auto-managed Documents section (Approach A) --------------------------
  // Inject the synthetic Documents section when the catalog has entries but the
  // published form has no DOCUMENT_SLOT field, so the uploader always appears.
  const effectiveSnapshot = React.useMemo<FormVersionSnapshot | null>(() => {
    if (!snapshot) return snapshot;
    const catalog = wiredBoundControls.documentCatalog;
    if (!catalog || catalog.length === 0) return snapshot;
    if (snapshotHasDocumentSlot(snapshot)) return snapshot;
    return withAutoDocumentsPage(snapshot);
  }, [snapshot, wiredBoundControls.documentCatalog]);

  // ---- Completion bar inputs (reuse already-held data; no new fetch) --------
  // Declared here — above the early returns below — so the `useMemo` obeys the
  // Rules of Hooks (a stable hook order on every render regardless of the
  // loading / error / no-snapshot branches). Fed to the CompletionBar header
  // render-prop (application-completion-tracker §C2/§C3).
  const completionSnapshot = (effectiveSnapshot ?? snapshot) as CompletionInput['snapshot'];
  const completionCatalog = (wiredBoundControls.documentCatalog ??
    []) as CompletionInput['documentCatalog'];
  const completionUploads = React.useMemo<CompletionInput['uploadedDocuments']>(
    () =>
      (wiredBoundControls.uploadedDocuments ?? []).map((doc) => ({
        catalogEntryId: doc.catalogEntryId ?? '',
      })),
    [wiredBoundControls.uploadedDocuments],
  );

  // ---- Per-field read-only predicate (Req 6.4, 21.4) ------------------------
  const isFieldDisabled = React.useCallback(
    (field: FormFieldDef, page: SnapshotPage): boolean => {
      // The document uploader is exempt from the post-approval Phase_1 lock.
      // Supplemental (non-required) document slots are meant to be collected
      // AFTER approval, so the uploader must stay mounted and editable even once
      // the application is APPROVED. The uploader locks its already-provided
      // required slots per-row (see DocumentSlotBoundControl), so this exemption
      // only opens the supplemental slots — the approved application's answers
      // stay read-only.
      const isDocumentField = field.bindingTarget === 'DOCUMENT_SLOT';
      // Once the application is APPROVED, Phase_1 is settled: every Phase_1 page
      // is presented read-only so the approved application's details can no
      // longer be edited. Phase_2 pages stay editable — they are the
      // post-approval work the contestant continues (application-form-builder
      // Req 16). A non-PHASE_2 page (PHASE_1 or the synthetic Documents page,
      // which is PHASE_1) is locked, except the document uploader (above).
      if (
        submissionStatus === SubmissionStatus.APPROVED &&
        page.phase !== 'PHASE_2' &&
        !isDocumentField
      ) {
        return true;
      }
      // APPROVED approval-required page → read-only for the applicant. (The
      // post-submission immutable-field lock is gone: a submitted application is
      // now rendered read-only by ApplicationStatusView, so the container never
      // mounts in a SUBMITTED state.)
      if (page.requiresDirectorApproval) {
        const status = pageApprovals?.[page.id] ?? 'PENDING';
        if (!isPageEditableByApplicant(status)) {
          return true;
        }
      }
      return false;
    },
    [pageApprovals, submissionStatus],
  );

  // ---- Document gate (the same pure rule the server's submit path runs) -----
  const computeMissingDocuments = React.useCallback((): DocumentCatalogEntry[] => {
    const catalog = wiredBoundControls.documentCatalog;
    if (!catalog || catalog.length === 0) {
      return [];
    }
    const applicantType = wiredBoundControls.applicantType;
    const uploaded = (wiredBoundControls.uploadedDocuments ?? []).map((doc) => ({
      catalogEntryId: doc.catalogEntryId ?? '',
    }));
    if (applicantType !== undefined) {
      return missingRequiredDocuments([...catalog], divisionKey, applicantType, uploaded);
    }
    // The applicant type isn't resolved yet (e.g. a division that doesn't map to
    // a legacy applicant type). The server STILL enforces every entry scoped to
    // all applicant types (null/'BOTH'), so evaluate those here rather than
    // silently passing the client gate and bouncing off a server rejection.
    // Type-specific (MISS/MRS) entries can't be asserted without the type, so
    // they are left to the server.
    const uploadedIds = new Set(uploaded.map((u) => u.catalogEntryId));
    return catalog.filter(
      (entry) =>
        (entry.applicantTypeScope == null || entry.applicantTypeScope === 'BOTH') &&
        (entry.visibleDivisionKeys == null ||
          entry.visibleDivisionKeys.length === 0 ||
          entry.visibleDivisionKeys.includes(divisionKey)) &&
        entry.requiredForSubmission &&
        !uploadedIds.has(entry.id),
    );
  }, [wiredBoundControls, divisionKey]);

  // ---- Save draft (Req 6.1) -------------------------------------------------
  const handleSaveDraft = React.useCallback(
    async (form: UseFormReturn<ApplicationResponseValues>): Promise<SaveOutcome> => {
      // A save already in flight or no loaded version: nothing is attempted, so
      // the auto-save indicator must not flip to saved/failed — report a skip.
      if (busy || !formVersionId) return 'skipped';
      // A locked / phase-closed Application is read-only — never initiate a write
      // (the server would reject it anyway; this is the client mirror). R9.2, R9.4.
      if (isLocked) return 'skipped';
      setBusy('draft');
      setActionError(null);
      setSubmitErrors([]);
      try {
        // Read the live values directly — no strict-schema gate — so a draft is
        // never blocked on missing required fields (Req 6.1).
        const values = form.getValues();
        const saved = await applicationFormClient.saveDraft({
          competitionId,
          formVersionId,
          values: values as ApplicationResponse['values'],
        });
        setResponse(saved);
        onSavedDraft?.(saved);
        return 'saved';
      } catch (err) {
        const message = err instanceof Error ? err.message : null;
        const lockKind = classifyPhaseLockError(message);
        if (lockKind) {
          // A server-authoritative phase-closed / locked rejection: surface the
          // SPECIFIC phase-and-Season message rather than the raw error (R9.5).
          setServerLock({ kind: lockKind, message: phaseLockMessage(lockKind, seasonLabel) });
        } else {
          setActionError(message ?? 'We could not save your draft. Please try again.');
        }
        return 'error';
      } finally {
        setBusy(null);
      }
    },
    [busy, formVersionId, competitionId, onSavedDraft, isLocked, seasonLabel],
  );

  // ---- Submit (Req 6.3, 7.1, 7.6, 20.3) -------------------------------------
  const submitValidated = React.useCallback(
    async (values: ApplicationResponseValues) => {
      if (busy || !formVersionId) return;
      // Prevent initiating a submission when the phase is closed / the Application
      // is locked (`isPhase1Open === false` / `isPhase2Unlocked === false` /
      // effectiveLock), mirroring the server (R9.2, R9.4, R16.5, R17.2).
      if (isLocked) return;

      // Client validation has already passed (form.handleSubmit). Run the
      // document gate before the network call (Req 20.3).
      const missing = computeMissingDocuments();
      if (missing.length > 0) {
        setSubmitErrors(
          missing.map((entry) => ({
            fieldId: entry.id,
            code: 'MISSING_REQUIRED_DOCUMENT' as const,
            message: `A required document is missing: ${entry.label}.`,
          })),
        );
        return;
      }

      setBusy('submit');
      setActionError(null);
      setSubmitErrors([]);
      try {
        // The server re-validates against the captured version before SUBMITTED
        // (Req 7.6). A blocked submit returns the naming errors instead.
        const result = await applicationFormClient.submit({
          competitionId,
          formVersionId,
          values: values as ApplicationResponse['values'],
        });
        if (result.ok && result.response) {
          setResponse(result.response);
          onSubmitted?.(result.response);
        } else {
          setSubmitErrors(result.errors ?? []);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : null;
        const lockKind = classifyPhaseLockError(message);
        if (lockKind) {
          // Map the server-authoritative phase-closed / locked rejection to the
          // SPECIFIC phase-and-Season message rather than a generic error (R9.5).
          setServerLock({ kind: lockKind, message: phaseLockMessage(lockKind, seasonLabel) });
        } else {
          setActionError(message ?? 'We could not submit your application. Please try again.');
        }
      } finally {
        setBusy(null);
      }
    },
    [busy, formVersionId, competitionId, computeMissingDocuments, onSubmitted, isLocked, seasonLabel],
  );

  // ---- Footer (save-draft / submit actions) ---------------------------------
  const renderFooter = React.useCallback(
    (form: UseFormReturn<ApplicationResponseValues>): React.ReactNode => (
      <div className="space-y-4">
        {submitErrors.length > 0 ? (
          <div
            role="alert"
            className="space-y-2 rounded-input border border-destructive-text/40 bg-destructive-text/5 p-4"
          >
            <p className="font-display text-body font-semibold text-destructive-text">
              Your application could not be submitted.
            </p>
            <ul className="list-disc space-y-1 pl-5 text-body-sm text-destructive-text">
              {submitErrors.map((error) => (
                <li key={`${error.fieldId}-${error.code}`}>{error.message}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {actionError ? (
          <p role="alert" className="rounded-input bg-destructive-text/5 px-3 py-2 text-body-sm text-destructive-text">
            {actionError}
          </p>
        ) : null}

        {isLocked ? (
          // Locked / phase-closed: the Application is read-only and no submit may
          // be initiated. The specific phase-and-Season message is shown in the
          // PhaseDeadlineBanner above; the submit/save-draft/auto-save affordances
          // are suppressed here (season-phase-deadlines R9.2, R9.4, R16.5, R17.1).
          null
        ) : isApproved ? (
          // APPROVED Phase_2 editing: no Submit step, no manual Save draft —
          // edits persist automatically (see ApprovedPhaseTwoAutoSave).
          <ApprovedPhaseTwoAutoSave
            form={form}
            onSave={handleSaveDraft}
            saving={busy === 'draft'}
          />
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              disabled={busy !== null}
              onClick={() => void handleSaveDraft(form)}
              data-testid="application-form-save-draft"
            >
              {busy === 'draft' ? 'Saving…' : 'Save draft'}
            </Button>
            <Button
              type="button"
              disabled={busy !== null}
              onClick={form.handleSubmit(submitValidated)}
              data-testid="application-form-submit"
            >
              {busy === 'submit' ? 'Submitting…' : 'Submit application'}
            </Button>
          </div>
        )}
      </div>
    ),
    [submitErrors, actionError, busy, handleSaveDraft, submitValidated, isApproved, isLocked],
  );

  // ---- Render ---------------------------------------------------------------
  if (load.status === 'loading') {
    return <CrownLoader message="Loading your application…" />;
  }

  if (load.status === 'error') {
    return (
      <div
        role="alert"
        data-testid="application-form-load-error"
        className="rounded-card border border-destructive/40 bg-destructive-muted p-4 text-sm text-destructive-text"
      >
        {load.message}
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div role="alert" className="rounded-card border border-destructive/40 bg-destructive-muted p-4 text-sm text-destructive-text">
        This application is not available yet.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Deadline display + phase-closed / locked notice for the current surface
          (season-phase-deadlines R8, R9, R13, R16.5, R17.1). */}
      <PhaseDeadlineBanner
        deadline={displayedDeadline}
        deadlineLabel={deadlineLabel}
        lock={lockState}
      />
      {/* When locked, the whole Application is presented greyed read-only: every
          control is rendered disabled via `disabled`, and the section tree is
          visually greyed and marked `aria-disabled` (R16.5, R17.1). */}
      <div
        aria-disabled={isLocked || undefined}
        data-testid={isLocked ? 'application-form-locked' : undefined}
        className={isLocked ? 'opacity-70' : undefined}
      >
        <FormRenderer
          snapshot={effectiveSnapshot ?? snapshot}
          competitionId={competitionId}
          divisionKey={divisionKey}
          activeDivisions={activeDivisions}
          applicationStatus={submissionStatus}
          defaultValues={defaultValues}
          disabled={isLocked}
          isFieldDisabled={isFieldDisabled}
          pageApprovals={pageApprovals}
          fileUploadAdapter={fileUploadAdapter}
          boundControls={wiredBoundControls}
          header={(form) => (
            <CompletionBarHeader
              form={form}
              snapshot={completionSnapshot}
              divisionKey={divisionKey}
              documentCatalog={completionCatalog}
              uploadedDocuments={completionUploads}
            />
          )}
          footer={renderFooter}
        />
      </div>
    </div>
  );
}

export default ApplicationFormContainer;
